import React, { useState } from "react";
import { motion } from "framer-motion";
import colors from "../styles/colors";
import { useTranslation } from "react-i18next";
import Button from "./Button";

interface WindowTypeProps {
  title: string;
  subtitle: string;
  image: string;
  buttonLink: string;
  gradientColors: [string, string];
  imageOnRight?: boolean;
}

const WindowType: React.FC<WindowTypeProps> = ({
  title,
  subtitle,
  image,
  buttonLink,
  gradientColors,
  imageOnRight = true,
}) => {
  const { t } = useTranslation();
  const [isImageLoading, setIsImageLoading] = useState(true);

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  return (
    <div
      className="w-full h-auto flex flex-col lg:flex-row items-center p-6 lg:p-12 mb-8 space-y-4 lg:space-y-0 lg:gap-x-16"
      style={{ backgroundColor: colors.pastelCream }}
    >
      {/* Image Section */}
      <div
        className={`w-full lg:w-[48%] flex justify-center items-center relative mb-8 lg:mb-0 ${
          imageOnRight ? "lg:order-last" : "lg:order-first"
        }`}
      >
        {isImageLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
          </div>
        )}
        <img
          src={image}
          alt={`${title} ${subtitle}`}
          loading="lazy"
          className={`w-full h-auto max-h-full object-cover rounded-lg shadow-lg ${
            isImageLoading ? "opacity-0" : "opacity-100"
          }`}
          onLoad={handleImageLoad}
        />
      </div>

      {/* Text Section */}
      <div
        className={`w-full lg:w-[40%] flex flex-col justify-center space-y-4 
          ${
            imageOnRight
              ? "lg:ml-auto lg:mr-0 lg:text-left"
              : "lg:ml-0 lg:mr-auto lg:text-right"
          }`}
      >
        <motion.h1
          className="text-5xl lg:text-7xl font-bold text-transparent bg-clip-text text-center lg:text-left"
          style={{
            backgroundImage: `linear-gradient(to right, ${gradientColors[0]}, ${gradientColors[1]})`,
          }}
          initial={{ opacity: 0, x: -80 }}
          whileInView={{
            opacity: 1,
            x: 0,
            transition: { delay: 0.2, duration: 0.5 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {title}
        </motion.h1>
        <motion.h2
          className="text-3xl lg:text-5xl font-thin text-center lg:text-left"
          style={{ color: colors.darkBrown }}
          initial={{ opacity: 0, x: -80 }}
          whileInView={{
            opacity: 1,
            x: 0,
            transition: { delay: 0.6, duration: 0.5 },
          }}
          viewport={{ once: false, amount: 0.5 }}
        >
          {subtitle}
        </motion.h2>

        {/* Button */}
        <Button
          to={buttonLink}
          text={t("windows.typeButton")}
          className="flex items-center justify-center space-x-2 text-sm md:text-base lg:text-lg  w-full md:w-auto lg:max-w-[400px]"
        />
      </div>
    </div>
  );
};

export default WindowType;
